// frontend/src/pages/customer/Products.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiShoppingCart,
  FiEye,
  FiFilter,
  FiGrid,
  FiList,
  FiPackage,
  FiTrendingUp,
  FiExternalLink
} from 'react-icons/fi';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseURL = process.env.REACT_APP_API_URL || '${process.env.REACT_APP_API_URL}';
  
  if (imagePath.startsWith('/uploads')) {
    return `${baseURL}${imagePath}`;
  }
  
  return `${baseURL}/uploads/${imagePath}`;
};

const Products = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loadCartCount, settings } = useOutletContext();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const [filters, setFilters] = useState({
    search: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    inStockOnly: false
  });

  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });

  // ✅ Smart Pagination Function
  const renderSmartPagination = () => {
    const pages = [];
    const totalPages = pagination.pages || 1;
    const currentPage = pagination.page || 1;
    const siblingCount = 2;
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    pages.push(1);
    
    const leftSibling = Math.max(currentPage - siblingCount, 2);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages - 1);
    
    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;
    
    if (!showLeftDots && showRightDots) {
      for (let i = 2; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
    } else if (showLeftDots && !showRightDots) {
      pages.push('...');
      for (let i = totalPages - 4; i < totalPages; i++) {
        pages.push(i);
      }
    } else if (showLeftDots && showRightDots) {
      pages.push('...');
      for (let i = leftSibling; i <= rightSibling; i++) {
        pages.push(i);
      }
      pages.push('...');
    } else {
      for (let i = 2; i < totalPages; i++) {
        pages.push(i);
      }
    }
    
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  useEffect(() => {
    const search = searchParams.get('search') || '';
    const category = searchParams.get('category') || '';
    
    setFilters(prev => ({
      ...prev,
      search,
      category
    }));
  }, [searchParams]);

  useEffect(() => {
    fetchProducts(1); // Reset to page 1 when filters change
  }, [filters]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customer/products', {
        params: {
          page,
          limit: 20,
          search: filters.search,
          category_id: filters.category,
          min_price: filters.minPrice,
          max_price: filters.maxPrice,
          in_stock: filters.inStockOnly || undefined
        }
      });

      if (response.data.success) {
        setProducts(response.data.data.products);
        setPagination(response.data.data.pagination);
        
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Fetch products error:', error);
      toast.error('Барааны жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      if (response.data.success) {
        setCategories(response.data.data.categories.filter(cat => cat.is_active));
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const addToCart = (product) => {
    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
    
    const existingItem = cart.find(item => item.productId === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({
        productId: product.id,
        quantity: 1
      });
    }

    localStorage.setItem('customerCart', JSON.stringify(cart));
    
    if (loadCartCount) {
      loadCartCount();
    }
    
    toast.success(`"${product.name}" сагсанд нэмэгдлээ`);
  };

  const handleBannerClick = (link) => {
    if (link) {
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.open(link, '_blank');
      } else {
        navigate(link);
      }
    }
  };

  return (
    <div className="products-page">
      <div className="container-custom py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Бүтээгдэхүүн</h1>
            <p className="text-gray-600 mt-1">
              {pagination.total || 0} бараа олдлоо
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiGrid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiList size={20} />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar + Banners */}
          <aside className="lg:w-64 flex-shrink-0 space-y-4">
            {/* Filters Card */}
            <div className="card sticky top-4 lg:top-24">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                <FiFilter className="text-blue-600" />
                <h3 className="font-semibold text-gray-900">Шүүлтүүр</h3>
              </div>

              <div className="space-y-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ангилал
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => {
                      const categoryId = e.target.value;
                      if (categoryId) {
                        navigate(`/customer/products?category=${categoryId}`);
                      } else {
                        navigate('/customer/products');
                      }
                    }}
                    className="input text-sm"
                  >
                    <option value="">Бүгд</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Үнийн хязгаар
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Хамгийн бага"
                      value={filters.minPrice}
                      onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                      className="input text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Хамгийн их"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                      className="input text-sm"
                    />
                  </div>
                </div>

                {/* Stock Filter */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.inStockOnly}
                      onChange={(e) => setFilters({ ...filters, inStockOnly: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Зөвхөн нөөцтэй</span>
                  </label>
                </div>

                {/* Reset Filters */}
                <button
                  onClick={() => {
                    setFilters({
                      search: '',
                      category: '',
                      minPrice: '',
                      maxPrice: '',
                      inStockOnly: false
                    });
                    navigate('/customer/products');
                  }}
                  className="btn btn-secondary w-full text-sm"
                >
                  Шүүлтүүр цэвэрлэх
                </button>
              </div>
            </div>

            {/* Sidebar Banners - Desktop only */}
            {settings && (
              <div className="hidden lg:block space-y-4">
                {/* Banner 1 */}
                {settings.sidebar_banner_1 && (
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleBannerClick(settings.sidebar_banner_1_link)}
                  >
                    <img
                      src={`${process.env.REACT_APP_API_URL}${settings.sidebar_banner_1}`}
                      alt="Баннер 1"
                      className="w-full h-auto object-cover"
                    />
                    {settings.sidebar_banner_1_link && (
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md">
                        <FiExternalLink size={14} className="text-gray-600" />
                      </div>
                    )}
                  </div>
                )}

                {/* Banner 2 */}
                {settings.sidebar_banner_2 && (
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleBannerClick(settings.sidebar_banner_2_link)}
                  >
                    <img
                      src={`${process.env.REACT_APP_API_URL}${settings.sidebar_banner_2}`}
                      alt="Баннер 2"
                      className="w-full h-auto object-cover"
                    />
                    {settings.sidebar_banner_2_link && (
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md">
                        <FiExternalLink size={14} className="text-gray-600" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-20">
                <div className="spinner-lg mx-auto mb-4"></div>
                <p className="text-gray-600">Уншиж байна...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <FiPackage size={64} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 text-lg">Бараа олдсонгүй</p>
                <p className="text-gray-500 text-sm mt-2">Өөр шүүлтүүр ашиглана уу</p>
              </div>
            ) : (
              <>
                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {products.map(product => (
                      <div key={product.id} className="product-card">
                        {/* Product Image */}
                        <div
                          className="product-image-wrapper cursor-pointer"
                          onClick={() => navigate(`/customer/products/${product.id}`)}
                        >
                          {product.image_url ? (
                            <>
                              <img
                                src={getImageUrl(product.image_url)}
                                alt={product.name}
                                className="product-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const placeholder = e.target.parentElement.querySelector('.product-image-placeholder');
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }}
                              />
                              <div 
                                className="product-image-placeholder"
                                style={{ display: 'none' }}
                              >
                                <div className="text-center">
                                  <FiPackage size={48} className="text-gray-300 mb-2" />
                                  <p className="text-xs text-gray-400">Зураг татагдсангүй</p>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="product-image-placeholder">
                              <div className="text-center">
                                <FiPackage size={48} className="text-gray-300 mb-2" />
                                <p className="text-xs text-gray-400">Зураг байхгүй</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Stock Badge */}
                          {(product.stock_quantity || 0) <= 0 && (
                            <div className="badge-stock out-of-stock">
                              Дууссан
                            </div>
                          )}
                          
                          {(product.stock_quantity || 0) > 0 && (product.stock_quantity || 0) < 10 && (
                            <div className="badge-stock low-stock">
                              Цөөн үлдсэн
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="product-info">
                          <h3 className="product-name">{product.name}</h3>
                          
                          {/* Prices */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Жижиглэн:</span>
                              <span className="text-sm font-semibold text-gray-900">
                                ₮{(product.retail_price || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">
                                Бөөний ({product.wholesale_quantity || 1}+):
                              </span>
                              <span className="text-sm font-semibold text-blue-600">
                                ₮{(product.wholesale_price || product.retail_price || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {/* Stock */}
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            <FiPackage size={14} />
                            <span>Үлдэгдэл: {product.stock_quantity || 0}</span>
                          </div>

                          {/* Actions */}
                          <div className="mt-4 flex gap-2">
                            <button
                              onClick={() => navigate(`/customer/products/${product.id}`)}
                              className="btn btn-outline flex-1 text-sm py-2"
                            >
                              <FiEye className="mr-1" size={16} />
                              Харах
                            </button>
                            <button
                              onClick={() => addToCart(product)}
                              disabled={(product.stock_quantity || 0) <= 0}
                              className="btn btn-primary flex-1 text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FiShoppingCart className="mr-1" size={16} />
                              Сагслах
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <div className="space-y-4">
                    {products.map(product => (
                      <div key={product.id} className="product-list-item">
                        <div
                          className="product-list-image cursor-pointer"
                          onClick={() => navigate(`/customer/products/${product.id}`)}
                        >
                          {product.image_url ? (
                            <>
                              <img 
                                src={getImageUrl(product.image_url)}
                                alt={product.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const placeholder = e.target.nextElementSibling;
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }}
                              />
                              <div 
                                className="flex items-center justify-center h-full bg-gray-100"
                                style={{ display: 'none' }}
                              >
                                <FiPackage size={40} className="text-gray-300" />
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full bg-gray-100">
                              <FiPackage size={40} className="text-gray-300" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3
                            className="product-name cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/customer/products/${product.id}`)}
                          >
                            {product.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {product.description || 'Дэлгэрэнгүй мэдээлэл байхгүй'}
                          </p>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <FiPackage size={14} />
                              Үлдэгдэл: {product.stock_quantity}
                            </span>
                            <span className="flex items-center gap-1">
                              <FiTrendingUp size={14} />
                              Бөөний: {product.wholesale_quantity}+
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <p className="text-xs text-gray-500">Жижиглэн</p>
                            <p className="text-lg font-bold text-gray-900">
                              {product.retail_price.toLocaleString()}₮
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-blue-600">
                              Бөөний ({product.wholesale_quantity}+)
                            </p>
                            <p className="text-lg font-bold text-blue-600">
                              {product.wholesale_price.toLocaleString()}₮
                            </p>
                          </div>
                          
                          <button
                            onClick={() => addToCart(product)}
                            disabled={product.stock_quantity <= 0}
                            className="btn btn-primary text-sm whitespace-nowrap"
                          >
                            <FiShoppingCart className="mr-1" size={16} />
                            Сагслах
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ✅ SMART PAGINATION */}
                {pagination.pages > 1 && (
                  <div className="flex flex-wrap justify-center items-center gap-2 mt-8 mb-4">
                    {/* Previous Button */}
                    <button
                      onClick={() => fetchProducts(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                        pagination.page === 1
                          ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                          : 'border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                      }`}
                    >
                      ← Өмнөх
                    </button>

                    {/* Page Numbers with Dots */}
                    <div className="flex items-center gap-1">
                      {renderSmartPagination().map((page, index) => {
                        if (page === '...') {
                          return (
                            <span 
                              key={`dots-${index}`} 
                              className="px-3 py-2 text-gray-400 select-none"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => fetchProducts(page)}
                            className={`min-w-[44px] px-4 py-2 rounded-lg border font-medium transition-all ${
                              pagination.page === page
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => fetchProducts(Math.min(pagination.pages, pagination.page + 1))}
                      disabled={pagination.page === pagination.pages}
                      className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                        pagination.page === pagination.pages
                          ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                          : 'border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                      }`}
                    >
                      Дараах →
                    </button>

                    {/* Page Info */}
                    <span className="ml-3 text-sm text-gray-600 font-medium">
                      {pagination.page} / {pagination.pages}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;